/**
 * Analyse heuristique d'une facture d'énergie française (électricité ou gaz).
 *
 * Conçu pour tourner dans le navigateur (analyse locale, zéro données envoyées)
 * ET potentiellement côté serveur (pas d'import Browser-only).
 *
 * Lexique ciblé : factures EDF, Engie, TotalEnergies, Eni, Vattenfall, Ohm,
 * Primeo, Alpiq, Elmy, Ilek, Mega, Proxelia, Ekwateur…
 */

/* ────────────────────────────────────────────────────────────────────────────
   Types publics
──────────────────────────────────────────────────────────────────────────── */

export type BillEnergyType = 'electricity' | 'gas' | 'unknown';
export type BillSource = 'pdf-text' | 'ocr' | 'unknown';
export type BillConfidence = 'high' | 'medium' | 'low';
export type ContractType = 'fixed' | 'indexed' | 'regulated' | 'unknown';

export interface BillTaxes {
  /** Contribution au Service Public de l'Electricité — ancienne dénomination ou "Accise" */
  cspe: boolean;
  cspeAmountEur: number | null;
  /** Tarif d'Utilisation du Réseau Public d'Électricité */
  turpe: boolean;
  turpeAmountEur: number | null;
  /** Taxe sur la Valeur Ajoutée */
  tva: boolean;
  /** Contribution Tarifaire d'Acheminement (gaz) */
  cta: boolean;
  /** Taxe Intérieure de Consommation sur le Gaz Naturel */
  ticgn: boolean;
  /** Accise sur les gaz naturels (terme post-2023 en remplacement de TICGN) */
  acciseGaz: boolean;
}

export interface BillInsights {
  /** Source de l'extraction */
  source: BillSource;
  /** Niveau de confiance global de l'analyse */
  confidence: BillConfidence;
  /** Type d'énergie détecté sur la facture */
  energyType: BillEnergyType;
  /** Nom du fournisseur détecté */
  supplierName: string | null;
  /** Consommation en kWh (valeur la plus haute trouvée : annuelle ou totale période) */
  annualConsumptionKwh: number | null;
  /** Puissance souscrite en kVA ou kW */
  subscribedPowerKva: number | null;
  /** Montant TTC de la facture (€) */
  totalAmountEur: number | null;
  /** Montant HT fourniture seule (€) — pour calculer le prix unitaire */
  supplyAmountHt: number | null;
  /** Part acheminement / distribution (€) */
  routingAmountEur: number | null;
  /** Prix unitaire calculé : €/kWh HT sur la fourniture */
  unitPriceEurPerKwh: number | null;
  /** Type de contrat détecté (fixé, indexé, réglementé) */
  contractType: ContractType;
  /** Date d'échéance du contrat si mentionnée (ISO YYYY-MM-DD) */
  contractExpiryDate: string | null;
  /**
   * Signal de sur-puissance : vrai si la puissance souscrite semble très élevée
   * par rapport à la consommation détectée (indicateur d'optimisation TURPE possible).
   */
  overPowerSignal: boolean;
  /** Taxes détectées */
  taxes: BillTaxes;
  /** Date de la facture (ISO YYYY-MM si trouvée) */
  billPeriod: string | null;
  /** Numéro de PDL / PCE / RAE / PRM si trouvé */
  pointDeliveryId: string | null;
  /**
   * Leviers d'optimisation suggérés par l'analyse — pré-cochés côté client
   * sans intervention manuelle de l'utilisateur.
   */
  suggestedLevers: Array<'turpe' | 'cspe'>;
  /**
   * Score de potentiel d'économie [0–100].
   * Agrège le nombre et la force des leviers détectés.
   * Utilisé pour afficher la jauge dans la carte de résumé.
   */
  savingsScore: number;
}

export interface BillInsightsInput {
  text: string;
  source: BillSource;
}

/**
 * Comparaison saisonnière : calculée côté client quand les deux factures
 * (été + hiver) sont disponibles. Retournée par `compareBills()`.
 */
export interface SeasonalComparison {
  summerKwh: number;
  winterKwh: number;
  /** Ratio hiver/été (> 1 = hiver plus énergivore, normal pour chauffage) */
  seasonalRatio: number;
  /** Variation en % entre été et hiver */
  variationPct: number;
  /** Profil de consommation déduit */
  profile: 'heating_dominated' | 'cooling_dominated' | 'stable' | 'unknown';
  /** Commentaire consultant — aide à cibler la meilleure offre */
  consultantNote: string;
}

/* ────────────────────────────────────────────────────────────────────────────
   Référentiels de benchmarks sectoriels (conso électricité kWh/an par salarié)
   Sources : ADEME, observatoire des fluides IMBE, bilans énergie filières.
   Valeurs médianes — approximations volontairement prudentes.
──────────────────────────────────────────────────────────────────────────── */

/** kWh/an par ETP (équivalent temps plein) — médiane sectorielle */
const SECTOR_KWH_PER_EMPLOYEE: Record<string, number> = {
  restaurant: 15000,
  boulangerie: 35000,
  hotel: 20000,
  bureau: 3000,
  entrepot: 12000,
  industrie_legere: 30000,
  industrie_lourde: 150000,
  agriculture: 8000,
  commerce: 8000,
  sante: 10000,
  enseignement: 4000,
};

/** Préfixes NAF → clé benchmark */
const NAF_SECTOR_BENCHMARK: Array<{ prefix: string; key: string }> = [
  { prefix: '56', key: 'restaurant' },
  { prefix: '551', key: 'hotel' },
  { prefix: '55', key: 'hotel' },
  { prefix: '107', key: 'boulangerie' },
  { prefix: '10', key: 'industrie_legere' },
  { prefix: '11', key: 'industrie_legere' },
  { prefix: '13', key: 'industrie_legere' },
  { prefix: '14', key: 'industrie_legere' },
  { prefix: '15', key: 'industrie_legere' },
  { prefix: '16', key: 'industrie_legere' },
  { prefix: '17', key: 'industrie_legere' },
  { prefix: '18', key: 'industrie_legere' },
  { prefix: '19', key: 'industrie_lourde' },
  { prefix: '20', key: 'industrie_lourde' },
  { prefix: '21', key: 'industrie_lourde' },
  { prefix: '22', key: 'industrie_lourde' },
  { prefix: '23', key: 'industrie_lourde' },
  { prefix: '24', key: 'industrie_lourde' },
  { prefix: '25', key: 'industrie_lourde' },
  { prefix: '26', key: 'industrie_lourde' },
  { prefix: '27', key: 'industrie_lourde' },
  { prefix: '28', key: 'industrie_lourde' },
  { prefix: '29', key: 'industrie_lourde' },
  { prefix: '30', key: 'industrie_lourde' },
  { prefix: '01', key: 'agriculture' },
  { prefix: '02', key: 'agriculture' },
  { prefix: '47', key: 'commerce' },
  { prefix: '46', key: 'entrepot' },
  { prefix: '52', key: 'entrepot' },
  { prefix: '86', key: 'sante' },
  { prefix: '87', key: 'sante' },
  { prefix: '85', key: 'enseignement' },
  { prefix: '84', key: 'bureau' },
  { prefix: '69', key: 'bureau' },
  { prefix: '70', key: 'bureau' },
  { prefix: '71', key: 'bureau' },
  { prefix: '72', key: 'bureau' },
  { prefix: '73', key: 'bureau' },
  { prefix: '74', key: 'bureau' },
];

export interface SectorBenchmark {
  sectorKey: string;
  /** Consommation médiane du secteur pour ce volume d'effectif (kWh/an) */
  medianKwh: number;
  /** Ratio client / médiane secteur (> 1 = client consomme plus que la médiane) */
  ratio: number | null;
  /** Interprétation */
  interpretation: 'above_median' | 'below_median' | 'at_median' | 'unknown';
}

/* ────────────────────────────────────────────────────────────────────────────
   Référentiel fournisseurs connus (détection souple)
──────────────────────────────────────────────────────────────────────────── */

const SUPPLIER_PATTERNS: Array<{ name: string; pattern: RegExp }> = [
  { name: 'EDF', pattern: /\bedf\b/i },
  { name: 'Engie', pattern: /\bengie\b/i },
  { name: 'TotalEnergies', pattern: /\btotalenergies\b|\btotal.{0,3}energies\b/i },
  { name: 'Eni', pattern: /\beni\b(?!\w)/i },
  { name: 'Vattenfall', pattern: /\bvattenfall\b/i },
  { name: 'Alpiq', pattern: /\balpiq\b/i },
  { name: 'Ohm Energie', pattern: /\bohm.{0,5}(?:energie|énergie)\b/i },
  { name: 'Primeo', pattern: /\bprimeo\b/i },
  { name: 'Elmy', pattern: /\belmy\b/i },
  { name: 'Ilek', pattern: /\bilek\b/i },
  { name: 'Mega Energie', pattern: /\bmega.{0,5}(?:energie|énergie|energy)\b/i },
  { name: 'Proxelia', pattern: /\bproxelia\b/i },
  { name: 'Ekwateur', pattern: /\bekwateur\b/i },
  { name: 'Plenitude', pattern: /\bplenitude\b|\bplénitude\b/i },
  { name: 'Endesa', pattern: /\bendesa\b/i },
  { name: 'Dyneff', pattern: /\bdyneff\b/i },
  { name: 'Gaz de Bordeaux', pattern: /\bgaz.{0,4}bordeaux\b/i },
  { name: 'GEG', pattern: /\bgeg\b/i },
  { name: 'Lucia Energie', pattern: /\blucia.{0,5}(?:energie|énergie)\b/i },
  { name: 'Synelva', pattern: /\bsynelva\b/i },
  { name: 'Enovos', pattern: /\benovos\b/i },
  { name: 'Alterna', pattern: /\balterna\b/i },
];

/* ────────────────────────────────────────────────────────────────────────────
   Prix marché de référence (moyennes B2B françaises, actualisées annuellement)
   Source : OFATE, CRE, indices PEG
──────────────────────────────────────────────────────────────────────────── */

/** Prix de référence du marché B2B (€/kWh HT) pour comparer le prix unitaire client */
const MARKET_REFERENCE_PRICES = {
  electricity: { low: 0.095, median: 0.135, high: 0.175 },
  gas: { low: 0.040, median: 0.065, high: 0.090 },
} as const;

/* ────────────────────────────────────────────────────────────────────────────
   Helpers regex
──────────────────────────────────────────────────────────────────────────── */

/** Extrait un nombre décimal au format français (ex: "1 234,56" ou "1234.56") */
function parseAmount(str: string): number | null {
  const cleaned = str.replace(/\s/g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

/**
 * Cherche un nombre décimal dans une fenêtre après un motif.
 * Accepte : 1 234,56 | 1234.56 | 1234,56 | 1 234.56
 */
function findAmountAfter(text: string, pattern: RegExp): number | null {
  const match = pattern.exec(text);
  if (!match) return null;
  const win = text.slice(match.index + match[0].length, match.index + match[0].length + 160);
  const numMatch = /(\d[\d\s]{0,8}(?:[.,]\d{1,2})?)/.exec(win);
  if (!numMatch?.[1]) return null;
  return parseAmount(numMatch[1]);
}

/** Trouve la plus grande valeur en kWh dans le texte (conso annuelle ou totale) */
function findKwh(text: string): number | null {
  const re = /(\d[\d\s.,]{1,10})\s*kWh/gi;
  let m: RegExpExecArray | null;
  let best: number | null = null;
  while ((m = re.exec(text)) !== null) {
    const v = parseAmount(m[1] ?? '');
    if (v !== null && (best === null || v > best)) best = v;
  }
  return best;
}

/** Cherche la puissance souscrite en kVA ou kW (fourchette B2B 3–5000) */
function findPower(text: string): number | null {
  const re = /(\d+(?:[.,]\d+)?)\s*k(?:VA|W|va|w)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const v = parseAmount(m[1] ?? '');
    if (v !== null && v >= 3 && v <= 5000) return v;
  }
  return null;
}

/** Cherche le montant TTC de la facture */
function findTotalTtc(text: string): number | null {
  return (
    findAmountAfter(text, /total\s+(?:TTC|ttc)/i) ??
    findAmountAfter(text, /montant\s+(?:TTC|ttc)/i) ??
    findAmountAfter(text, /net\s+[àa]\s+payer/i) ??
    findAmountAfter(text, /votre\s+facture\s+s['']élève\s+[àa]/i) ??
    null
  );
}

/** Cherche le montant HT de fourniture d'énergie */
function findSupplyHt(text: string): number | null {
  return (
    findAmountAfter(text, /fourniture\s+(?:HT|ht)/i) ??
    findAmountAfter(text, /energie\s+(?:HT|ht)/i) ??
    findAmountAfter(text, /total\s+(?:HT|ht)/i) ??
    null
  );
}

/** Cherche le montant d'acheminement */
function findRoutingAmount(text: string): number | null {
  return (
    findAmountAfter(text, /achemin(?:ement|eur)/i) ??
    findAmountAfter(text, /distribution\b/i) ??
    findAmountAfter(text, /\bTURPE\b/i) ??
    null
  );
}

/** Cherche une période de facturation (ISO YYYY-MM) */
function findBillPeriod(text: string): string | null {
  const MONTHS: Record<string, string> = {
    janvier: '01', février: '02', mars: '03', avril: '04',
    mai: '05', juin: '06', juillet: '07', août: '08',
    septembre: '09', octobre: '10', novembre: '11', décembre: '12',
  };

  const reDateSlash = /(\d{2})\/(\d{4})/;
  const reMonthYear =
    /(janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre)\s+(\d{4})/i;

  const m1 = reDateSlash.exec(text);
  if (m1) return `${m1[2]}-${m1[1]}`;

  const m2 = reMonthYear.exec(text);
  if (m2?.[1] && m2[2]) {
    const mm = MONTHS[m2[1].toLowerCase()];
    if (mm) return `${m2[2]}-${mm}`;
  }
  return null;
}

/** Cherche le numéro de point de livraison (PDL / PCE / PRM) */
function findPointDelivery(text: string): string | null {
  const re = /(?:PDL|PCE|PRM|RAE|EAN)[:\s]+(\d{10,16})/i;
  const m = re.exec(text);
  return m?.[1] ?? null;
}

/** Détecte le type de contrat (fixé, indexé, réglementé) */
function detectContractType(text: string): ContractType {
  if (/prix\s+fix[eé]|tarif\s+fix[eé]|offre\s+fix[eé]e|contrat\s+fix[eé]/i.test(text)) {
    return 'fixed';
  }
  if (
    /prix\s+index[eé]|tarif\s+index[eé]|index[eé]\s+(?:sur|au)\s+(?:PEG|Spot|EPEX|ARENH|marché)/i.test(text) ||
    /ARENH|spot\s+(?:horaire|journalier)|PEG\s+(?:Nord|Sud)|NCG\b|TTF\b/i.test(text)
  ) {
    return 'indexed';
  }
  if (/tarif\s+r[eé]glement[eé]|TRV|TRVE|ATRD|bleu|jaune|vert\s+(?:A|B|C)/i.test(text)) {
    return 'regulated';
  }
  return 'unknown';
}

/** Cherche une date d'échéance de contrat (format DD/MM/YYYY ou mention de fin) */
function findContractExpiry(text: string): string | null {
  // Cherche "échéance le XX/XX/XXXX" ou "fin de contrat : XX/XX/XXXX"
  const re =
    /(?:écheance|échéance|fin\s+(?:du\s+)?contrat|expir(?:e|ation))\s*[:\s]+(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/i;
  const m = re.exec(text);
  if (m) {
    const [, dd, mm, yyyy] = m;
    if (dd && mm && yyyy) {
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
  }
  return null;
}

/** Détecte le type d'énergie depuis le texte */
function detectEnergyType(text: string): BillEnergyType {
  const hasElecStrong = /électricité|electricite|élec\b|kVA|PDL\b|TURPE|Enedis|ERDF/i.test(text);
  const hasGazStrong =
    /\bgaz\s+naturel|\bGNL\b|\bPCE\b|\bGRDF\b|\bTICGN\b|\bATRD\b|accise.*gaz|CTA\b/i.test(text);

  const hasKwh = /\bkWh\b/i.test(text);

  if (hasGazStrong && !hasElecStrong) return 'gas';
  if (hasElecStrong) return 'electricity';
  if (hasKwh) return 'electricity';
  return 'unknown';
}

/**
 * Détecte un signal de sur-puissance souscrite.
 * Heuristique : en B2B réseau BT, une consommation annuelle de X kWh
 * correspond approximativement à X/8760 × coeff_utilisation kW de puissance active.
 * Si la puissance souscrite dépasse largement (~3×) la puissance estimée, signal activé.
 */
function detectOverPower(kwh: number | null, kva: number | null): boolean {
  if (!kwh || !kva) return false;
  // Heures d'utilisation typiques B2B : 1500–3000 h/an. On prend 2000 h.
  const estimatedKw = kwh / 2000;
  // Si puissance souscrite > 3× la puissance estimée → sur-puissance probable
  return kva > estimatedKw * 3 && kva > 18; // seuil minimum 18 kVA pour éviter les faux positifs
}

/**
 * Calcule le score de potentiel d'économie [0–100].
 * - Levier TURPE : +25
 * - Levier CSPE éligible : +25
 * - Contrat réglementé (optimisable vers marché) : +20
 * - Contrat indexé (risque prix) : +15
 * - Sur-puissance détectée : +15
 * - Prix unitaire > médiane marché : +10 par tranche de 15 %
 */
function computeSavingsScore(params: {
  turpe: boolean;
  cspe: boolean;
  contractType: ContractType;
  overPower: boolean;
  unitPrice: number | null;
  energyType: BillEnergyType;
}): number {
  let score = 0;
  if (params.turpe) score += 25;
  if (params.cspe) score += 25;
  if (params.contractType === 'regulated') score += 20;
  else if (params.contractType === 'indexed') score += 15;
  if (params.overPower) score += 15;
  if (params.unitPrice && params.energyType !== 'unknown') {
    const ref = MARKET_REFERENCE_PRICES[params.energyType as 'electricity' | 'gas'];
    const overMarket = params.unitPrice / ref.median - 1;
    if (overMarket > 0.15) score += 10;
    if (overMarket > 0.30) score += 5;
  }
  return Math.min(100, score);
}

/* ────────────────────────────────────────────────────────────────────────────
   Analyseur principal
──────────────────────────────────────────────────────────────────────────── */

export function analyzeBillText({ text, source }: BillInsightsInput): BillInsights {
  // --- Fournisseur
  let supplierName: string | null = null;
  for (const s of SUPPLIER_PATTERNS) {
    if (s.pattern.test(text)) {
      supplierName = s.name;
      break;
    }
  }

  const energyType = detectEnergyType(text);
  const annualConsumptionKwh = findKwh(text);
  const subscribedPowerKva = findPower(text);
  const totalAmountEur = findTotalTtc(text);
  const supplyAmountHt = findSupplyHt(text);
  const routingAmountEur = findRoutingAmount(text);
  const billPeriod = findBillPeriod(text);
  const pointDeliveryId = findPointDelivery(text);
  const contractType = detectContractType(text);
  const contractExpiryDate = findContractExpiry(text);
  const overPowerSignal = detectOverPower(annualConsumptionKwh, subscribedPowerKva);

  // Prix unitaire : montant fourniture HT ÷ conso kWh
  let unitPriceEurPerKwh: number | null = null;
  if (supplyAmountHt && annualConsumptionKwh && annualConsumptionKwh > 0) {
    const raw = supplyAmountHt / annualConsumptionKwh;
    // Fourchette plausible : 0.01 à 0.50 €/kWh
    if (raw >= 0.01 && raw <= 0.5) unitPriceEurPerKwh = raw;
  }

  // --- Taxes
  const cspe =
    /\bCSPE\b/i.test(text) ||
    /accise\s+sur\s+l[''](?:électricité|electricite)/i.test(text) ||
    /contribution\s+au\s+service\s+public\s+de\s+l[''](?:électricité|electricite)/i.test(text);

  const cspeAmountEur = cspe
    ? (findAmountAfter(text, /\bCSPE\b/i) ??
       findAmountAfter(text, /accise\s+sur\s+l[''](?:électricité|electricite)/i) ??
       null)
    : null;

  const turpe =
    /\bTURPE\b/i.test(text) ||
    /tarif\s+d['']utilisation\s+du\s+r[eé]seau/i.test(text) ||
    /achemin(?:ement|eur)/i.test(text);

  const turpeAmountEur = turpe
    ? (findAmountAfter(text, /\bTURPE\b/i) ??
       findAmountAfter(text, /achemin(?:ement|eur)/i) ??
       null)
    : null;

  const tva = /\bTVA\b/i.test(text);
  const cta =
    /\bCTA\b/i.test(text) ||
    /contribution\s+tarifaire\s+d['']achemin/i.test(text);
  const ticgn =
    /\bTICGN\b/i.test(text) ||
    /taxe\s+int[eé]rieure\s+de\s+consommation.*gaz/i.test(text);
  const acciseGaz =
    /accise\s+sur\s+les?\s+gaz/i.test(text) ||
    /accise.*gaz\s+naturel/i.test(text);

  // --- Confiance globale
  let score = 0;
  if (supplierName) score += 2;
  if (annualConsumptionKwh) score += 2;
  if (totalAmountEur) score += 2;
  if (energyType !== 'unknown') score += 1;
  if (subscribedPowerKva) score += 1;
  if (turpe || cspe || tva) score += 1;
  if (billPeriod) score += 1;
  const confidence: BillConfidence = score >= 7 ? 'high' : score >= 4 ? 'medium' : 'low';

  // --- Leviers suggérés automatiquement
  const suggestedLevers: Array<'turpe' | 'cspe'> = [];
  if (turpe) suggestedLevers.push('turpe');
  if (cspe) suggestedLevers.push('cspe');

  // --- Score potentiel économies
  const savingsScore = computeSavingsScore({
    turpe,
    cspe,
    contractType,
    overPower: overPowerSignal,
    unitPrice: unitPriceEurPerKwh,
    energyType,
  });

  return {
    source,
    confidence,
    energyType,
    supplierName,
    annualConsumptionKwh,
    subscribedPowerKva,
    totalAmountEur,
    supplyAmountHt,
    routingAmountEur,
    unitPriceEurPerKwh,
    contractType,
    contractExpiryDate,
    overPowerSignal,
    taxes: { cspe, cspeAmountEur, turpe, turpeAmountEur, tva, cta, ticgn, acciseGaz },
    billPeriod,
    pointDeliveryId,
    suggestedLevers,
    savingsScore,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Comparaison saisonnière été / hiver
──────────────────────────────────────────────────────────────────────────── */

export function compareBills(
  summer: BillInsights,
  winter: BillInsights,
): SeasonalComparison | null {
  const s = summer.annualConsumptionKwh;
  const w = winter.annualConsumptionKwh;
  if (!s || !w) return null;

  const ratio = w / s;
  const variationPct = ((w - s) / s) * 100;

  let profile: SeasonalComparison['profile'] = 'stable';
  let consultantNote =
    "Consommation stable entre été et hiver — profil peu sensible à la saisonnalité. Une offre à prix fixe sur 1 ou 2 ans est adaptée.";

  if (ratio > 1.5) {
    profile = 'heating_dominated';
    consultantNote =
      "Forte saisonnalité chauffage : consommation hiver très supérieure à l'été. Les offres avec modulation saisonnière ou puissance variable peuvent générer des économies.";
  } else if (ratio < 0.65) {
    profile = 'cooling_dominated';
    consultantNote =
      "Consommation estivale dominante (climatisation, chambre froide, process de refroidissement). Une analyse de la puissance souscrite en été est recommandée.";
  } else if (Math.abs(variationPct) > 20) {
    profile = 'stable';
    consultantNote =
      "Légère saisonnalité — consommation globalement stable. Offre fixe ou indexée à court terme selon votre aversion au risque prix.";
  }

  return { summerKwh: s, winterKwh: w, seasonalRatio: ratio, variationPct, profile, consultantNote };
}

/* ────────────────────────────────────────────────────────────────────────────
   Benchmark sectoriel
──────────────────────────────────────────────────────────────────────────── */

export function computeSectorBenchmark(
  nafCode: string | null | undefined,
  annualKwh: number | null,
  employeeCount: string | null | undefined,
): SectorBenchmark | null {
  if (!annualKwh) return null;

  const normalizedNaf = nafCode?.replace(/[\s.]/g, '').toUpperCase() ?? '';
  let sectorKey: string | null = null;

  for (const len of [3, 2]) {
    const prefix = normalizedNaf.slice(0, len);
    const entry = NAF_SECTOR_BENCHMARK.find((e) => e.prefix === prefix);
    if (entry) { sectorKey = entry.key; break; }
  }

  if (!sectorKey) return null;

  const kwhPerEmployee = SECTOR_KWH_PER_EMPLOYEE[sectorKey];
  if (!kwhPerEmployee) return null;

  // Estime le nombre d'ETP depuis la tranche d'effectif INSEE
  let estimatedEmployees = 10; // par défaut : PME
  if (employeeCount) {
    const n = parseInt(employeeCount.replace(/\D/g, ''), 10);
    if (!isNaN(n) && n > 0) estimatedEmployees = n;
  }

  const medianKwh = kwhPerEmployee * estimatedEmployees;
  const ratio = annualKwh / medianKwh;

  let interpretation: SectorBenchmark['interpretation'] = 'at_median';
  if (ratio > 1.2) interpretation = 'above_median';
  else if (ratio < 0.8) interpretation = 'below_median';

  return { sectorKey, medianKwh, ratio, interpretation };
}

/* ────────────────────────────────────────────────────────────────────────────
   Comparaison prix marché
──────────────────────────────────────────────────────────────────────────── */

export interface MarketPriceComparison {
  unitPrice: number;
  marketMedian: number;
  /** Surcoût relatif par rapport à la médiane marché (peut être négatif) */
  overCostRatio: number;
  /** Niveau de prix */
  level: 'cheap' | 'market' | 'expensive' | 'very_expensive';
}

export function compareToMarket(
  unitPrice: number | null,
  energyType: BillEnergyType,
): MarketPriceComparison | null {
  if (!unitPrice || energyType === 'unknown') return null;
  const ref = MARKET_REFERENCE_PRICES[energyType as 'electricity' | 'gas'];
  const overCostRatio = unitPrice / ref.median - 1;

  let level: MarketPriceComparison['level'] = 'market';
  if (overCostRatio > 0.25) level = 'very_expensive';
  else if (overCostRatio > 0.08) level = 'expensive';
  else if (overCostRatio < -0.08) level = 'cheap';

  return { unitPrice, marketMedian: ref.median, overCostRatio, level };
}

/* ────────────────────────────────────────────────────────────────────────────
   Résumé textuel pour le consultant
──────────────────────────────────────────────────────────────────────────── */

export function formatInsightsSummary(insights: BillInsights): string {
  const lines: string[] = ['[Analyse facture automatique]'];

  if (insights.supplierName) lines.push(`Fournisseur détecté : ${insights.supplierName}`);
  if (insights.energyType !== 'unknown') {
    lines.push(`Énergie : ${insights.energyType === 'electricity' ? 'Électricité' : 'Gaz'}`);
  }
  if (insights.contractType !== 'unknown') {
    const ctLabels: Record<ContractType, string> = {
      fixed: 'Prix fixé', indexed: 'Indexé marché', regulated: 'Tarif réglementé', unknown: ''
    };
    lines.push(`Type contrat : ${ctLabels[insights.contractType]}`);
  }
  if (insights.contractExpiryDate) {
    lines.push(`Échéance contrat : ${insights.contractExpiryDate}`);
  }
  if (insights.annualConsumptionKwh) {
    lines.push(`Consommation : ${insights.annualConsumptionKwh.toLocaleString('fr-FR')} kWh`);
  }
  if (insights.subscribedPowerKva) {
    lines.push(`Puissance souscrite : ${insights.subscribedPowerKva} kVA`);
  }
  if (insights.overPowerSignal) {
    lines.push('⚠ Sur-puissance souscrite détectée → levier TURPE');
  }
  if (insights.unitPriceEurPerKwh) {
    lines.push(`Prix unitaire HT : ${(insights.unitPriceEurPerKwh * 100).toFixed(2)} c€/kWh`);
  }
  if (insights.totalAmountEur) {
    lines.push(`Montant TTC : ${insights.totalAmountEur.toLocaleString('fr-FR')} €`);
  }
  if (insights.taxes.turpe) {
    const amt = insights.taxes.turpeAmountEur
      ? ` (${insights.taxes.turpeAmountEur.toLocaleString('fr-FR')} €)`
      : '';
    lines.push(`TURPE/acheminement détecté${amt} → levier optimisation`);
  }
  if (insights.taxes.cspe) {
    const amt = insights.taxes.cspeAmountEur
      ? ` (${insights.taxes.cspeAmountEur.toLocaleString('fr-FR')} €)`
      : '';
    lines.push(`CSPE/Accise détectée${amt} → vérifier éligibilité remboursement`);
  }
  if (insights.savingsScore > 0) {
    lines.push(`Score potentiel économies : ${insights.savingsScore}/100`);
  }
  if (insights.billPeriod) lines.push(`Période : ${insights.billPeriod}`);
  lines.push(`Confiance : ${insights.confidence} · source : ${insights.source}`);

  return lines.join('\n');
}
